import { extractErrorMessage } from '@/lib/errorUtils';
import { branchFormService } from '@/services/branchFormService';
import {
  BranchFormSubmissionResponse,
  CombinedBranchFormData,
} from '@/types/branch.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Hook for branch token validation
 */
export const useBranchTokenValidation = (
  token: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['validateBranchToken', token],
    queryFn: () => branchFormService.validateToken(token),
    retry: false,
    staleTime: 0, // Always check token freshness
    gcTime: 0, // Don't cache token validation results
    enabled: options?.enabled !== false, // Default to enabled unless explicitly disabled
  });
};

/**
 * Hook for submitting branch form data
 *
 * @param token - Validation token for submission
 * @param onSuccessCallback - Optional callback to execute after successful submission (e.g., reset form)
 */
export const useBranchFormSubmission = (
  token: string,
  onSuccessCallback?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation<
    BranchFormSubmissionResponse,
    Error,
    CombinedBranchFormData
  >({
    mutationFn: (data: CombinedBranchFormData) =>
      branchFormService.submitBranchForm(token, data),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branchContacts'] });

      // Execute success callback (e.g., reset form state and localStorage)
      if (onSuccessCallback) {
        onSuccessCallback();
      }

      toast.success('Success', {
        description: 'Branch information submitted successfully',
      });
    },
    onError: error => {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to submit branch information'
      );
      toast.error('Error', {
        description: errorMessage,
      });
    },
  });
};
